import { Badge, Card, Text, Tooltip } from '@radix-ui/themes';
import clsx from 'clsx';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import type { OpenMessageDocument } from '@hubertai/screening-question-types';
import { EPreferredScoringLanguage, useScoreInterview } from '../scoring.context';

interface Props extends ComponentProps<'div'> {
  answer: OpenMessageDocument;
}

const CandidateOpenAnswer = ({ answer, ...props }: Props) => {
  const [text, setText] = useState<string>('');
  const { preferredLanguage } = useScoreInterview();

  useEffect(() => {
    if (preferredLanguage && preferredLanguage === EPreferredScoringLanguage.English && answer.translations?.en) {
      setText(answer.translations.en);
    } else {
      // @ts-ignore
      if (answer.utterance) {
        // @ts-ignore
        setText(answer.utterance);
      } else {
        setText(answer.texts.join(' '));
      }
    }
  }, [preferredLanguage]);

  const parsed = answer.parsed;
  const intent = parsed?.intent?.intent_name;
  const modelName = parsed?.intent?.model_name;
  const relevance = answer?.turn_parse?.relevance;

  const badgeStyle = {
    fontSize: '8px',
    fontWeight: 500,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    opacity: 0.8,
  };

  return (
    <div {...props} className={clsx('flex flex-col items-end justify-end gap-2')}>
      <Card className={clsx('max-w-[80%]', 'flex flex-row justify-end', 'p-2')}>
        <Text as="div" size="2" color="gray">
          {/* Candidate-typed text — must never be rendered as HTML (XSS) */}
          <div>{text}</div>
        </Text>
      </Card>
      <div className={clsx('flex flex-row items-center justify-end gap-2')}>
        {relevance && relevance.gating_label && relevance.gating_label !== 'on_topic' ? (
          <Tooltip
            content={
              <div>
                <div>Action: {relevance.action === 're-ask' ? 'Re-ask' : 'Accept'}</div>
                <div>Confidence: {relevance.confidence.toFixed(2)}</div>
                <div>Labels: {relevance.labels.join(', ')}</div>
                <div>Reasons: {relevance.reasons}</div>
                <div>Relevance score: {relevance.relevance_score.toFixed(2)}</div>
              </div>
            }
          >
            <Badge variant="soft" color="blue" size="1" className="w-fit" style={badgeStyle}>
              {relevance.gating_label.split('_').join(' ')}
            </Badge>
          </Tooltip>
        ) : null}

        {intent ? (
          <Tooltip
            content={
              <div>
                <div>Intent: {intent ? intent.split('_').join(' ') : 'unknown'}</div>
                <div>Model: {modelName ? modelName.split('_').join(' ') : 'unknown'}</div>
              </div>
            }
          >
            <Badge variant="soft" color={intent ? 'blue' : 'red'} size="1" style={badgeStyle}>
              {intent ? intent.split('_').join(' ') : 'unknown'}
            </Badge>
          </Tooltip>
        ) : null}

        {answer.reusage && answer.reusage.is_reused ? (
          <Badge variant="soft" color="blue" size="1" style={badgeStyle}>
            {answer.reusage?.is_edited ? 'Reused answer: edited' : 'Reused answer: Not edited'}
          </Badge>
        ) : null}

        {answer.channel && answer.channel.length ? (
          <Badge variant="soft" color="blue" size="1" style={badgeStyle}>
            {answer.channel.split('_').join(' ')}
          </Badge>
        ) : null}
      </div>
    </div>
  );
};

export default CandidateOpenAnswer;
